import { isAxiosError } from "axios";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { CopyPageGroupsSummary } from "./CopyPageGroupsSummary";
import { usePageGroupOperation } from "./use-page-group-operation";

import { serverCopyPageGroups } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Card } from "mds/components/Card";
import { FormField } from "mds/components/FormField";
import { Listbox } from "mds/components/Listbox";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronDownIcon, LoadingDotsIcon, PageListIcon, PersonGroupIcon } from "mds/icons";
import { slices, store, storeApi } from "store/index";
import {
  pageSelectors,
  selectAuthorizedGroupPagesWithTopicId,
  topicSelectors,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { getReadableAxiosError } from "utils/alerts";
import { Link } from "utils/routing";
import { pageUrl } from "utils/urls";

export const CopyPageGroups = ({
  pageId,
  disabled,
  isInherited = false,
  isTemplate = false,
}: {
  pageId: string;
  disabled: boolean;
  isInherited?: boolean;
  isTemplate?: boolean;
}) => {
  const sourceForm = useForm();
  const { isPending, runPageGroupOperation } = usePageGroupOperation(pageId);
  const controlsDisabled = disabled || isPending;
  const [editingPageId, setEditingPageId] = useState<string>();
  const [draft, setDraft] = useState<{
    pageId: string;
    savedSourceId: string | null;
    topicId?: string;
    sourcePageId?: string;
  }>();
  const [isCopying, setIsCopying] = useState(false);
  const [error, setError] = useState<string>();
  const pending = useRef<symbol | null>(null);
  useEffect(
    () => () => {
      // A replaced picker must not restore its source after navigation.
      pending.current = null;
    },
    [pageId],
  );
  const page = useObjectSelector(
    (state) => pageSelectors.selectById(state, pageId),
    ["copied_groups_from_page_id", "has_copied_groups"],
  );
  const pages = useListSelector(selectAuthorizedGroupPagesWithTopicId, [
    "id",
    "title",
    "topic_id",
    "section_id",
  ]);
  const topics = useListSelector(topicSelectors.filterForCurrentSection, ["id", "title"]);
  const sourcePage = pages.find((candidate) => candidate.id === page?.copied_groups_from_page_id);
  const currentDraft =
    draft?.pageId === pageId && draft.savedSourceId === (page?.copied_groups_from_page_id ?? null)
      ? draft
      : undefined;
  const topicId = currentDraft?.topicId ?? (sourcePage ? sourcePage.topic_id || "none" : undefined);
  const pageOptions: { id: string; title: string }[] = [];
  for (const candidate of pages) {
    if (candidate.id !== pageId && (candidate.topic_id || "none") === topicId) {
      pageOptions.push({ id: candidate.id, title: candidate.title });
    }
  }

  const isEditing = editingPageId === pageId;
  const hasSource = !!page?.copied_groups_from_page_id;
  const showEditor = !isInherited && (isEditing || !hasSource || isCopying);
  const selectedSourceId = currentDraft?.sourcePageId ?? page?.copied_groups_from_page_id;

  const selectedSource = pageOptions.find((option) => option.id === selectedSourceId);

  const copyGroups = async (sourcePageId: string) => {
    if (pending.current || disabled) return;
    await runPageGroupOperation(async () => {
      const request = Symbol("copyGroupsRequest");
      pending.current = request;
      setIsCopying(true);
      setError(undefined);
      try {
        await serverCopyPageGroups(pageId, sourcePageId);
        if (pending.current !== request) return;
        setDraft(undefined);
        setEditingPageId(undefined);
        store.dispatch(
          slices.pages.actions.patchOne({
            id: pageId,
            copied_groups_from_page_id: sourcePageId,
            has_copied_groups: !isTemplate,
          }),
        );
        try {
          await storeApi.pages.retrieve(pageId, {
            skipCache: true,
            skipToast: true,
            shouldApplyResponse: () => pending.current === request,
          });
        } catch {
          if (pending.current === request) {
            setError(t("group_configuration_card.copy_refresh_failed"));
          }
        }
      } catch (err) {
        if (pending.current !== request) return;
        setError(
          isAxiosError(err)
            ? getReadableAxiosError(err)
            : t("group_configuration_card.copy_failed"),
        );
      } finally {
        if (pending.current === request) {
          pending.current = null;
          setIsCopying(false);
        }
      }
    });
  };

  return (
    <div className={clsx("flex w-full min-w-0 flex-col gap-2", !showEditor && "px-2")}>
      {!showEditor && (
        <div className="body-s flex w-full flex-wrap items-center justify-between gap-2">
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1 gap-y-1">
            {t(
              isTemplate || isInherited || !page?.has_copied_groups
                ? "group_configuration_card.configured_source"
                : "group_configuration_card.copied_source",
            )}{" "}
            {sourcePage ? (
              <SquareButton
                as={Link}
                className="min-w-0 max-w-full"
                kind="ghost"
                rel="noopener noreferrer"
                size="xs"
                target="_blank"
                title={t("group_configuration_card.open_source_groups")}
                to={pageUrl(sourcePage.section_id, sourcePage.id, {
                  additionalParams: { groups: "1" },
                })}
              >
                <span
                  aria-hidden="true"
                  className="flex size-5 shrink-0 items-center justify-center rounded-full bg-hibiscus-tint-95 text-hibiscus"
                >
                  <PersonGroupIcon className="max-h-3 max-w-3" size="xs" />
                </span>
                <span className="min-w-0 truncate">{sourcePage.title}</span>
              </SquareButton>
            ) : (
              <span>{t("group_configuration_card.source_missing")}</span>
            )}
          </span>
          {!isInherited && (
            <LinkButton
              className="shrink-0"
              disabled={controlsDisabled}
              size="s"
              onClick={() => setEditingPageId(pageId)}
            >
              {t("common.edit")}
            </LinkButton>
          )}
        </div>
      )}
      {!showEditor && !isTemplate && !isCopying && !page?.has_copied_groups && sourcePage && (
        <CopyPageGroupsSummary pageId={sourcePage.id} />
      )}
      {showEditor && (
        <FormProvider {...sourceForm}>
          <Card className="flex flex-col gap-3 px-5 pb-5 pt-3">
            <div>
              <h3 className="h3 text-blue">{t("group_configuration_card.select_copy_source")}</h3>
              <p className="body-xs text-black-tint-40">
                {t("group_configuration_card.copy_source_help")}
              </p>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-3 pt-0.5">
              <FormField
                className="min-w-0 flex-[1_1_160px]"
                disabled={controlsDisabled}
                label={t("group_configuration_card.source_topic")}
                name="sourceTopic"
                required
              >
                {(controlProps) => (
                  <Listbox
                    {...controlProps}
                    buttonSize="s"
                    buttonText={topicId ? undefined : t("breakout_group_modal.select_a_topic")}
                    className="w-full"
                    eventName="Groups - Copy source topic"
                    items={[
                      ...topics.map((topic) => ({
                        id: topic.id,
                        title: topic.title,
                        iconStart: <PageListIcon className="shrink-0" />,
                      })),
                      { id: "none", title: t("breakout_group_modal.no_topic") },
                    ]}
                    selectedValue={topicId}
                    onSelectionChange={(topic) => {
                      setDraft({
                        pageId,
                        savedSourceId: page?.copied_groups_from_page_id ?? null,
                        topicId: topic.id,
                      });
                      setError(undefined);
                    }}
                  />
                )}
              </FormField>
              <FormField
                className="min-w-0 flex-[1_1_160px]"
                disabled={controlsDisabled || !pageOptions.length}
                label={t("group_configuration_card.source_page")}
                name="sourcePage"
                required
              >
                {(controlProps) => (
                  <Listbox
                    {...controlProps}
                    eventName="Groups - Copy source page"
                    items={pageOptions}
                    selectedValue={selectedSource?.id}
                    triggerButton={
                      <SquareButton
                        aria-busy={isCopying}
                        className="w-full min-w-0 justify-between gap-2"
                        kind="tertiary"
                        size="s"
                      >
                        {isCopying && (
                          <span aria-hidden="true" className="inline-flex shrink-0">
                            <LoadingDotsIcon />
                          </span>
                        )}
                        <span className="flex min-w-0 items-center gap-2">
                          {selectedSource && (
                            <span
                              aria-hidden="true"
                              className="flex size-5 shrink-0 items-center justify-center rounded-full bg-hibiscus-tint-95 text-hibiscus"
                            >
                              <PersonGroupIcon className="max-h-3 max-w-3" size="xs" />
                            </span>
                          )}
                          <span className="min-w-0 truncate">
                            {selectedSource?.title ?? t("breakout_group_modal.select_a_group_page")}
                          </span>
                        </span>
                        {!isCopying && (
                          <span aria-hidden="true" className="inline-flex shrink-0">
                            <ChevronDownIcon />
                          </span>
                        )}
                      </SquareButton>
                    }
                    onSelectionChange={(candidate) => {
                      setDraft({
                        topicId,
                        pageId,
                        savedSourceId: page?.copied_groups_from_page_id ?? null,
                        sourcePageId: candidate.id,
                      });
                      setError(undefined);
                      if (isTemplate) copyGroups(candidate.id);
                    }}
                  />
                )}
              </FormField>
            </div>
            {topicId && !pageOptions.length && <p>{t("tooltip.no_group_page_available")}</p>}
            {!isTemplate && !isCopying && selectedSource && (
              <CopyPageGroupsSummary pageId={selectedSource.id} />
            )}
            {!isTemplate && (
              <div className="flex justify-end gap-2 pt-0.5">
                {hasSource && (
                  <SquareButton
                    disabled={isPending}
                    kind="ghost"
                    size="s"
                    onClick={() => {
                      setDraft(undefined);
                      setEditingPageId(undefined);
                      setError(undefined);
                    }}
                  >
                    {t("common.cancel")}
                  </SquareButton>
                )}
                <SquareButton
                  disabled={controlsDisabled || !selectedSource}
                  kind="accent"
                  size="s"
                  onClick={() => selectedSource && copyGroups(selectedSource.id)}
                >
                  {t("group_configuration_card.copy_groups_action")}
                </SquareButton>
              </div>
            )}
          </Card>
        </FormProvider>
      )}
      {isInherited && (
        <div className="flex justify-end">
          <SquareButton
            disabled={controlsDisabled || !hasSource}
            kind="accent"
            size="s"
            onClick={() =>
              page?.copied_groups_from_page_id && copyGroups(page.copied_groups_from_page_id)
            }
          >
            {t("group_configuration_card.copy_groups_action")}
          </SquareButton>
        </div>
      )}
      {isCopying && (
        <span className="sr-only" role="status">
          {t("group_configuration_card.copying")}
        </span>
      )}
      {error && (
        <div role="alert">
          <Banner kind="error">{error}</Banner>
        </div>
      )}
    </div>
  );
};
